<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-overlay v-if="loading.getDailyRestaurant" :absolute="true">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-data-table
      v-if="report"
      :items="customSearch(foods, headers, filters.search)"
      :headers="headers"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading.getDailyRestaurant"
    >
      <template v-slot:item.menu="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" :title="'ID: ' + item.id">
              {{ item.menu == $t("total") ? $t("totalU") : translate(item.menu) }}
            </span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template v-slot:body.append>
        <tr>
          <td></td>
          <td style="font-weight: bold">{{ $t("totalU") }}</td>
          <td style="font-weight: bold">{{ totalShift("shift_one") }}</td>
          <td style="font-weight: bold">{{ totalShift("shift_two") }}</td>
          <td style="font-weight: bold">{{ totalShift("shift_three") }}</td>
          <td style="font-weight: bold">{{ totalShift("shift_four") }}</td>
          <td style="font-weight: bold">{{ totalShift("total") }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  mixins: [hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    report: null,
    loading: {
      getDailyRestaurant: false,
    },
  }),
  methods: {
    async getDailyRestaurant() {
      if (this.loading.getDailyRestaurant) {
        console.warn(
          "Get daily restaurant operation already in progress, skipping duplicate request"
        );
        return;
      }

      if (!this.restaurantId) return;

      this.loading.getDailyRestaurant = true;

      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };

      try {
        const res = await reportService.getDailyRestaurant(
          this.$store.state.restaurant.id,
          params
        );
        this.report = res.data;
      } catch (error) {
        console.error("Error fetching daily restaurant report:", error);
      } finally {
        this.loading.getDailyRestaurant = false;
      }
    },
    totalShift(shiftKey) {
      return this.foods.reduce((acc, food) => acc + (food[shiftKey] || 0), 0);
    },
  },
  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    foods() {
      if (!this.report) return [];
      const foodTotals = {};

      // Helper function to add quantities to the foodTotals object
      const addFoods = (shift, shiftKey) => {
        if(!shift) return;
        Object.entries(shift).forEach(([mealName, mealData]) => {
          mealData.foods.forEach((food) => {
            if (!foodTotals[food.name]) {
              foodTotals[food.name] = {
                // num will be added later
                id: food.id,
                menu: this.translate(food.name),
                shift_one: 0,
                shift_two: 0,
                shift_three: 0,
                shift_four: 0,
                total: 0,
              };
            }
            foodTotals[food.name][shiftKey] += mealData.quantity;
            foodTotals[food.name].total += mealData.quantity;
          });
        });
      };

      // Process each shift
      if (this.report["1-smena"]) {
        addFoods(this.report["1-smena"], "shift_one");
      }
      if (this.report["2-smena"]) {
        addFoods(this.report["2-smena"], "shift_two");
      }
      if (this.report["3-smena"]) {
        addFoods(this.report["3-smena"], "shift_three");
      }
      if (this.report["4-smena"]) {
        addFoods(this.report["3-smena"], "shift_four");
      }

      // Convert the foodTotals object to an array and add num property
      return Object.values(foodTotals).map((food, index) => ({
        ...food,
        num: index + 1,
      }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("m"), value: "menu" },
        { text: this.$t("1st_shift"), value: "shift_one" },
        { text: this.$t("2nd_shift"), value: "shift_two" },
        { text: this.$t("3rd_shift"), value: "shift_three" },
        { text: this.$t("4th_shift"), value: "shift_four" },
        { text: this.$t("total"), value: "total" },
      ];
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.getDailyRestaurant();
      },
      deep: true,
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        this.printDocument({
          tables: [
            {
              headers: this.headers,
              items: [
                ...this.foods,
                {
                  menu: this.$t("Total"),
                  shift_one: this.totalShift("shift_one"),
                  shift_two: this.totalShift("shift_two"),
                  shift_three: this.totalShift("shift_three"),
                  shift_four: this.totalShift("shift_four"),
                  total: this.totalShift("total"),
                },
              ],
              showCaption: true,
            },
          ],
          title: [this.$t("report"), this.$t("meals_main")].join(": "),
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("meals_main")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("meals_main"),
              headers: this.headers,
              items: [
                ...this.foods,
                {
                  menu: this.$t("report"),
                  shift_one: this.totalShift("shift_one"),
                  shift_two: this.totalShift("shift_two"),
                  shift_three: this.totalShift("shift_three"),
                  shift_four: this.totalShift("shift_four"),
                  total: this.totalShift("total"),
                },
              ],
              title: [this.$t("report"), this.$t("meals_main")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
